import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { formatRateFraction } from "@bw/shared/types";
import { isApiError } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import type {
  ContractTermsResponse,
  PublishContractVersionRequest,
  OrgResponse,
  OrgContractTermsVersionResponse,
  CommercialFeeVersionPage,
} from "@bw/shared/types/generated";
import { formatDateUtc } from "@bw/shared/utils";

import { PlatformPage } from "@/components/PlatformPage";
import { StatusBadge } from "@/components/StatusBadge";
import { orgBusinessName, orgLegalName } from "@/shared/orgDisplay";

import {
  getContractTerms,
  getOrg,
  listContractVersions,
  publishContractVersion,
  applyDefaultContractTerms,
} from "../api";
import { ContractTermsEditor } from "../components/ContractTermsEditor";
import { useFeePolicyVersions } from "../hooks/useFeePolicies";
import {
  buildContractVersionRequest,
  createContractTermsDraft,
  latestContractHistoryEffectiveDate,
  type ContractTermsDraft,
} from "../contractTermsDraft";

interface PublishVariables {
  orgId: string;
  request: PublishContractVersionRequest;
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <Box component="div">
      <Typography component="dt" color="text.secondary" variant="caption">
        {label}
      </Typography>
      <Typography
        component="dd"
        variant="body2"
        sx={{ fontWeight: 600, m: 0, mt: 0.5 }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function termsHeading(terms: ContractTermsResponse | null): string {
  if (!terms) return "Contract terms";
  const isScheduled =
    terms.version_id != null && terms.version_id === terms.upcoming_version_id;
  return isScheduled ? "Scheduled terms" : "Current terms";
}

function coherentBindingPage(
  page: CommercialFeeVersionPage,
  orgId: string,
  requestedPage: { limit: number; offset: number },
): boolean {
  if (page.audience_org_id !== orgId || page.limit !== requestedPage.limit || page.offset !== requestedPage.offset
    || !Array.isArray(page.items) || !Array.isArray(page.binding_choices)) return false;
  if (page.items.some((item) => item.audience_org_id !== null && item.audience_org_id !== orgId)) return false;
  const seen = new Set<string>();
  return page.binding_choices.every((choice) => {
    const binding = choice?.binding, edition = choice?.edition;
    if (choice.readiness !== "available" || !binding?.selector || !edition?.selector
      || binding.fee_policy_version_id !== edition.fee_policy_version_id
      || binding.selector.service_key !== edition.selector.service_key
      || binding.selector.origin !== edition.selector.origin
      || binding.selector.purpose !== edition.selector.purpose
      || !Array.isArray(edition.rules) || !edition.rules.length) return false;
    const source = page.items.find((item) => item.fee_policy_version_id === binding.fee_policy_version_id);
    if (!source || source.effective_from !== edition.effective_from || source.effective_until !== edition.effective_until) return false;
    const key = `${binding.fee_policy_version_id}:${binding.selector.service_key}:${binding.selector.origin}:${binding.selector.purpose}`;
    if (seen.has(key)) return false;
    seen.add(key);
    const originalRules = source.rules.filter((rule) => rule.selector.service_key === binding.selector.service_key
      && rule.selector.origin === binding.selector.origin && rule.selector.purpose === binding.selector.purpose);
    if (originalRules.length !== edition.rules.length
      || new Set(originalRules.map((rule) => rule.component)).size !== originalRules.length) return false;
    const projectedComponents = new Set<string>();
    return edition.rules.every((rule) => {
      if (projectedComponents.has(rule.component)) return false;
      projectedComponents.add(rule.component);
      const original = originalRules.find((candidate) => candidate.component === rule.component);
      return original !== undefined && original.percentage === rule.percentage
        && JSON.stringify(original.flat_fee) === JSON.stringify(rule.flat_fee)
        && JSON.stringify(original.cap) === JSON.stringify(rule.cap)
        && original.campaign_stacking === rule.campaign_stacking && original.rounding === rule.rounding;
    });
  });
}

function TermsSummary({ terms }: { terms: ContractTermsResponse | null }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
          {termsHeading(terms)}
        </Typography>
        {terms ? (
          <Box
            component="dl"
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
              },
              gap: 2.5,
              m: 0,
              mt: 2.5,
            }}
          >
            <Fact
              label="Commission"
              value={formatRateFraction(terms.commission_rate)}
            />
            <Fact
              label="Payout cadence"
              value={terms.payout_schedule.frequency}
            />
            <Fact
              label="Payout timezone"
              value={terms.payout_schedule.timezone}
            />
            <Fact
              label="Effective"
              value={formatDateUtc(terms.effective_date)}
            />
            <Fact
              label="Grandfather / renewal"
              value={
                terms.termination_date
                  ? formatDateUtc(terms.termination_date)
                  : "No end date on file"
              }
            />
            <Fact
              label="Accepted"
              value={
                terms.accepted_at
                  ? formatDateUtc(terms.accepted_at)
                  : "No acceptance on file"
              }
            />
          </Box>
        ) : (
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            No contract terms are on file
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function ContractTermsWorkflow({ orgId }: { orgId: string }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const today = useRef(new Date().toISOString().slice(0, 10));
  const submitLock = useRef(false);
  const [workflowPending, setWorkflowPending] = useState(false);
  const [feeOffset, setFeeOffset] = useState(0);
  const [authorityDenied, setAuthorityDenied] = useState(false);
  const [reviewingAccess, setReviewingAccess] = useState(false);
  const [editorEpoch, setEditorEpoch] = useState(0);
  const [reviewedSources, setReviewedSources] = useState<{
    org: OrgResponse; terms: ContractTermsResponse | null;
    history: OrgContractTermsVersionResponse[]; fees: CommercialFeeVersionPage;
  } | null>(null);
  const [reviewedDenials, setReviewedDenials] = useState<{
    counts: number[]; feeOffset: number | undefined; mutationVariables: PublishVariables | undefined;
  }>({ counts: [0, 0, 0, 0], feeOffset: undefined, mutationVariables: undefined });
  const feePage = { limit: 50, offset: feeOffset };
  const feeQuery = useFeePolicyVersions(orgId, feePage);
  const feeData = reviewedSources?.fees.offset === feeOffset ? reviewedSources.fees : feeQuery.data;
  const invalidFeeMetadata = feeData !== undefined && !coherentBindingPage(feeData, orgId, feePage);
  const feeState = queryClient.getQueryState(["fee-policy-versions", user?.uid, orgId, 50, feeOffset]);
  const feeUnavailable = (feeQuery.isError && feeState?.status === "error") || invalidFeeMetadata;
  const feeReadPending = feeQuery.isPending || (!feeQuery.isSuccess && !reviewedSources);
  const bindingOptions = feeUnavailable ? [] : (feeData?.binding_choices ?? []).map((choice) => ({
    binding: choice.binding,
    label: `${choice.binding.selector.service_key === "classes" ? "Classes enrollment" : "Service fee terms"} — effective ${formatDateUtc(choice.edition.effective_from)}${choice.edition.effective_until ? ` until ${formatDateUtc(choice.edition.effective_until)}` : ""}`,
  }));

  async function readProtectedSource<T>(read: () => Promise<T>): Promise<T> {
    try {
      return await read();
    } catch (error) {
      // Fence the first actual authority denial, including Query's retry phase.
      // Only the explicit all-source review below may reveal protected facts.
      if (isApiError(error) && error.status === 403) setAuthorityDenied(true);
      throw error;
    }
  }

  function retryProtectedRead(failureCount: number, error: Error): boolean {
    if (isApiError(error) && error.status === 403) return false;
    // Preserve the surrounding query policy for ordinary transient failures.
    const policy = queryClient.getDefaultOptions().queries?.retry;
    if (typeof policy === "function") return policy(failureCount, error);
    return policy === true || failureCount < (typeof policy === "number" ? policy : policy === false ? 0 : 3);
  }

  const orgQuery = useQuery({
    queryKey: ["platform", "org", orgId, user?.uid],
    queryFn: () => readProtectedSource(() => getOrg(orgId)),
    retry: retryProtectedRead,
  });

  const termsQuery = useQuery({
    queryKey: ["platform", "contract-terms", orgId, user?.uid],
    queryFn: () => readProtectedSource(() => getContractTerms(orgId)),
    retry: retryProtectedRead,
  });

  const historyQuery = useQuery({
    queryKey: ["platform", "contract-versions", orgId, user?.uid],
    queryFn: () => readProtectedSource(() => listContractVersions(orgId)),
    retry: retryProtectedRead,
  });

  const readQueries = [orgQuery, termsQuery, historyQuery, feeQuery];
  const readKeys = [["platform", "org", orgId, user?.uid],
    ["platform", "contract-terms", orgId, user?.uid],
    ["platform", "contract-versions", orgId, user?.uid],
    ["fee-policy-versions", user?.uid, orgId, 50, feeOffset]];
  const newReadDenial = readQueries.some((query, index) => query.isError
    && isApiError(query.error) && query.error.status === 403
    && (queryClient.getQueryState(readKeys[index])?.errorUpdateCount ?? 0)
      > (index === 3 && reviewedDenials.feeOffset !== feeOffset ? 0 : reviewedDenials.counts[index]));
  const orgData = reviewedSources?.org ?? orgQuery.data;
  const termsData = reviewedSources ? reviewedSources.terms : termsQuery.data;
  const historyData = reviewedSources?.history ?? historyQuery.data;

  const publishMutation = useMutation({
    mutationFn: ({ orgId: owningOrgId, request }: PublishVariables) =>
      publishContractVersion(owningOrgId, request),
    retry: false,
    onSuccess: async (_response, variables) => {
      await Promise.allSettled([
        queryClient.invalidateQueries({
          queryKey: ["platform", "contract-versions", variables.orgId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["platform", "contract-terms", variables.orgId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["platform", "org", variables.orgId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["platform", "onboarding"],
        }),
        queryClient.invalidateQueries({ queryKey: ["platform", "orgs"] }),
      ]);
    },
  });

  const isReadError =
    orgQuery.isError ||
    termsQuery.isError ||
    historyQuery.isError ||
    (orgQuery.isSuccess && !orgQuery.data);
  const newMutationDenial = isApiError(publishMutation.error) && publishMutation.error.status === 403
    && publishMutation.variables !== reviewedDenials.mutationVariables;
  const currentDenial = newReadDenial || newMutationDenial;
  useEffect(() => {
    if (currentDenial) setAuthorityDenied(true);
  }, [currentDenial]);
  const isReadLoading =
    !isReadError &&
    (orgQuery.isLoading || termsQuery.isLoading || historyQuery.isLoading);
  const publishFailureMessage =
    isApiError(publishMutation.error) && publishMutation.error.status === 429
      ? "A contract version was already published today. Your draft is still here; review history or publish another version tomorrow."
      : undefined;

  function submit(draft: ContractTermsDraft) {
    if (submitLock.current || publishMutation.isPending || feeUnavailable || feeReadPending
      || authorityDenied || currentDenial || reviewingAccess) return;

    const request = buildContractVersionRequest(draft);
    if (!request) return;

    submitLock.current = true;
    setWorkflowPending(true);
    publishMutation.mutate(
      { orgId, request },
      {
        onSuccess: (_response, variables) => {
          navigate(`/organizations/${variables.orgId}/contract-versions`);
        },
        onSettled: () => {
          submitLock.current = false;
          setWorkflowPending(false);
        },
      },
    );
  }

  if (authorityDenied || currentDenial) {
    return (
      <PlatformPage title="Publish Contract Terms">
        <Alert severity="warning">
          Current Admin contract access unavailable. Review current access before continuing.{" "}
          <Button size="small" disabled={reviewingAccess} onClick={async () => {
            setReviewingAccess(true);
            try {
              const [org, terms, history, fees] = await Promise.all([
                orgQuery.refetch(), termsQuery.refetch(), historyQuery.refetch(), feeQuery.refetch(),
              ]);
              if (org.isSuccess && org.data.org_id === orgId && terms.isSuccess && history.isSuccess
                && history.data.every((version) => version.org_id === orgId)
                && fees.isSuccess && coherentBindingPage(fees.data, orgId, feePage)) {
                // Capture all actual reviewed replies before revealing fresh inputs;
                // observer-cache paint may lag refetch settlement.
                setReviewedSources({ org: org.data, terms: terms.data, history: history.data, fees: fees.data });
                setReviewedDenials({ counts: readKeys.map((key) =>
                  queryClient.getQueryState(key)?.errorUpdateCount ?? 0), feeOffset,
                  mutationVariables: publishMutation.variables });
                publishMutation.reset();
                setEditorEpoch((epoch) => epoch + 1);
                setAuthorityDenied(false);
              }
            } catch {
              // Failed current-source review cannot restore cached authority.
            } finally {
              setReviewingAccess(false);
            }
          }}>Review current access</Button>
        </Alert>
      </PlatformPage>
    );
  }

  if (isReadLoading) {
    return (
      <PlatformPage
        title="Publish Contract Terms"
        description="Publish one dated, immutable agreement with exact reviewed text, commercial terms, and approval references."
      >
        <Box
          role="status"
          aria-label="Loading contract terms"
          aria-live="polite"
          sx={{ display: "flex", justifyContent: "center", py: 8 }}
        >
          <CircularProgress size={36} />
        </Box>
      </PlatformPage>
    );
  }

  return (
    <PlatformPage
      title="Publish Contract Terms"
      description="Publish one dated, immutable agreement with exact reviewed text, commercial terms, and approval references."
      actions={<ReturnToDefaultButton orgId={orgId} />}
    >
      {isReadError ? (
        <Alert
          severity="error"
          role="alert"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                void Promise.allSettled([
                  orgQuery.refetch(),
                  termsQuery.refetch(),
                  historyQuery.refetch(),
                ]);
              }}
            >
              Retry
            </Button>
          }
        >
          Contract terms could not be loaded. Try again.
        </Alert>
      ) : orgData && termsQuery.isSuccess && historyData ? (
        <Stack spacing={3}>
          <Card variant="outlined">
            <CardContent>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{
                  alignItems: { sm: "flex-start" },
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    component="h2"
                    variant="h6"
                    sx={{ fontWeight: 700 }}
                  >
                    {orgBusinessName(orgData)}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    variant="body2"
                    sx={{ mt: 0.5 }}
                  >
                    {orgLegalName(orgData) ?? "Legal name not provided"}
                  </Typography>
                </Box>
                <StatusBadge status={orgData.status} />
              </Stack>
            </CardContent>
          </Card>

          <Box
            sx={{
              display: "grid",
              gap: 3,
              gridTemplateColumns: {
                xs: "1fr",
                lg: "minmax(0, 1.5fr) minmax(18rem, 1fr)",
              },
              alignItems: "start",
            }}
          >
            <ContractTermsEditor
              key={editorEpoch}
              defaultValues={createContractTermsDraft(
                termsData ?? null,
                today.current,
                latestContractHistoryEffectiveDate(historyData),
              )}
              isPending={workflowPending || publishMutation.isPending}
              publishFailed={publishMutation.isError}
              publishFailureMessage={publishFailureMessage}
              onSubmit={submit}
              bindingOptions={bindingOptions}
              bindingsLoading={feeQuery.isFetching || feeQuery.isPending}
              bindingsError={
                feeUnavailable ? "Fee editions unavailable. Refresh the current source before publishing." : null
              }
              onRefreshBindings={() => void feeQuery.refetch()}
              onViewHistory={() =>
                navigate(`/organizations/${orgId}/contract-versions`)
              }
            />
            {feeQuery.isSuccess && !feeUnavailable ? (
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Typography variant="body2">Published fee editions: {feeData?.items.length ?? 0} of {feeData?.total ?? 0}</Typography>
                <Button size="small" disabled={feeOffset === 0 || feeQuery.isFetching}
                  onClick={() => setFeeOffset(Math.max(0, feeOffset - 50))}>Previous editions</Button>
                <Button size="small" disabled={feeOffset + 50 >= (feeData?.total ?? 0) || feeQuery.isFetching}
                  onClick={() => setFeeOffset(feeOffset + 50)}>Next editions</Button>
              </Stack>
            ) : null}
            <TermsSummary terms={termsData ?? null} />
          </Box>
        </Stack>
      ) : null}
    </PlatformPage>
  );
}

/** Moves a business from custom terms back onto the universal platform default. */
function ReturnToDefaultButton({ orgId }: { orgId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const apply = useMutation({
    mutationFn: () => applyDefaultContractTerms(orgId),
    retry: false,
    onSuccess: async () => {
      setOpen(false);
      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: ["platform", "contract-versions", orgId] }),
        queryClient.invalidateQueries({ queryKey: ["platform", "contract-terms", orgId] }),
        queryClient.invalidateQueries({ queryKey: ["platform", "contract-defaults"] }),
      ]);
      navigate(`/organizations/${orgId}/contract-versions`);
    },
  });
  return (
    <>
      <Button variant="outlined" onClick={() => setOpen(true)}>
        Return to platform default
      </Button>
      <Dialog open={open} onClose={() => (apply.isPending ? undefined : setOpen(false))}>
        <DialogTitle>Return to the platform default?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This business receives the current platform default terms and follows future defaults.
            Its current terms stay in force for 60 days while the owner reviews and accepts.
          </DialogContentText>
          {apply.isError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              The default wasn’t applied. Try again.
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button disabled={apply.isPending} onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" disabled={apply.isPending} onClick={() => apply.mutate()}>
            {apply.isPending ? "Applying…" : "Use platform default"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export function ContractTermsFormPage() {
  const { user } = useAuth();
  const { orgId } = useParams<{ orgId: string }>();

  if (!orgId) {
    return <PlatformPage title="Publish Contract Terms" isError />;
  }

  return <ContractTermsWorkflow key={`${user?.uid}:${orgId}`} orgId={orgId} />;
}
